/**
 * URL-held drill-down state (dashboard design rules §4.2): filters, drawer
 * selections and chart drill targets live in the search params, so the back
 * button walks the drill path, links are shareable, and refresh keeps the view.
 *
 * Building blocks:
 * - `useDrillParams` — read/write a typed set of filter params in place.
 * - `useDrillNavigate` / `DrillLink` / `drillTo` — jump to another route (an
 *   L2 sub-route) carrying params, e.g. `/cloud/alerts?severity=Sev1`.
 * - `useDrawerParam` — an entity `Drawer` keyed by one param, URL-shareable.
 *
 * Multi-value params (a `FilterSelect` selection) are comma-joined; use
 * `getList` / pass a string array to `set`.
 */

import { type ReactNode, useCallback, useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";

/** A single param value on its way into the URL. `null`/`undefined`/`""` delete the param. */
export type DrillValue = string | number | boolean | readonly string[] | null | undefined;

const LIST_SEPARATOR = ",";

function serializeDrillValue(value: DrillValue): string | null {
  if (value == null || value === "" || value === false) {
    return null;
  }
  if (Array.isArray(value)) {
    const joined = value.filter(Boolean).join(LIST_SEPARATOR);
    return joined === "" ? null : joined;
  }
  return String(value);
}

/** `"a,b"` → `["a", "b"]`; `null`/`""` → `[]`. */
export function parseListParam(value: string | null | undefined): string[] {
  if (!value) {
    return [];
  }
  return value.split(LIST_SEPARATOR).filter(Boolean);
}

/** The search string (`?severity=Sev1&sub=x`) for a set of drill params, `""` when empty. */
export function drillSearch(params: Readonly<Record<string, DrillValue>>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    const serialized = serializeDrillValue(value);
    if (serialized != null) {
      search.set(key, serialized);
    }
  }
  const encoded = search.toString();
  return encoded ? `?${encoded}` : "";
}

/** A router path with drill params — `drillTo("/cloud/alerts", { severity: "Sev1" })`. */
export function drillTo(path: string, params?: Readonly<Record<string, DrillValue>>): string {
  return params ? `${path}${drillSearch(params)}` : path;
}

export interface DrillParams<K extends string> {
  /** Current raw value per key (`undefined` when absent). */
  params: Record<K, string | undefined>;
  /** Current multi-value list for a key (comma-decoded). */
  getList: (key: K) => string[];
  /**
   * Patch params in place: strings/numbers set, arrays comma-join, and
   * `null`/`undefined`/`""`/`[]` delete. Other params on the URL survive.
   * Pushes a history entry so the back button undoes the drill; pass
   * `{ replace: true }` for ephemeral changes (e.g. typing).
   */
  set: (patch: Partial<Record<K, DrillValue>>, options?: { replace?: boolean }) => void;
  /** Remove all of this hook's keys from the URL (one history entry). */
  clear: () => void;
}

/**
 * Typed read/write access to a fixed set of drill params on the current URL.
 *
 * ```tsx
 * const drill = useDrillParams("severity", "sub");
 * drill.set({ severity: "Sev1" });          // → ?severity=Sev1 (push)
 * drill.getList("sub");                     // → ["abc", "def"] from ?sub=abc,def
 * ```
 */
export function useDrillParams<K extends string>(...keys: readonly K[]): DrillParams<K> {
  const [searchParams, setSearchParams] = useSearchParams();

  const params = useMemo(() => {
    const current = {} as Record<K, string | undefined>;
    for (const key of keys) {
      current[key] = searchParams.get(key) ?? undefined;
    }
    return current;
    // keys is a rest parameter — a fresh array per render with stable contents.
  }, [searchParams, keys.join(" ")]);

  const getList = useCallback((key: K) => parseListParam(params[key]), [params]);

  const set = useCallback(
    (patch: Partial<Record<K, DrillValue>>, options?: { replace?: boolean }) => {
      setSearchParams(
        (previous) => {
          const next = new URLSearchParams(previous);
          for (const [key, value] of Object.entries(patch)) {
            const serialized = serializeDrillValue(value as DrillValue);
            if (serialized == null) {
              next.delete(key);
            } else {
              next.set(key, serialized);
            }
          }
          return next;
        },
        { replace: options?.replace ?? false },
      );
    },
    [setSearchParams],
  );

  const clear = useCallback(() => {
    setSearchParams((previous) => {
      const next = new URLSearchParams(previous);
      for (const key of keys) {
        next.delete(key);
      }
      return next;
    });
  }, [setSearchParams, keys.join(" ")]);

  return { params, getList, set, clear };
}

/**
 * Imperative drill navigation for chart `onClick` handlers (marks are not
 * anchors): `navigateDrill("/cloud/alerts", { severity: datum.severity })`.
 */
export function useDrillNavigate(): (
  path: string,
  params?: Readonly<Record<string, DrillValue>>,
) => void {
  const navigate = useNavigate();
  return useCallback((path, params) => navigate(drillTo(path, params)), [navigate]);
}

export interface DrillLinkProps {
  /** Target route (an L2 sub-route or board). */
  to: string;
  /** Drill params carried on the URL. */
  params?: Readonly<Record<string, DrillValue>>;
  children: ReactNode;
  className?: string;
  "aria-label"?: string;
}

/**
 * A router `<Link>` that carries drill params — the "everything aggregated is
 * a door" affordance for tiles, captions and "View all N →" rows.
 *
 * ```tsx
 * <DrillLink to="/cloud/alerts" params={{ severity: "Sev1" }}>View all 12 →</DrillLink>
 * ```
 */
export function DrillLink({ to, params, children, className, ...rest }: DrillLinkProps) {
  return (
    <Link to={drillTo(to, params)} className={className} aria-label={rest["aria-label"]}>
      {children}
    </Link>
  );
}

export interface DrawerParam {
  /** The currently open entity id under this key, or `null`. */
  value: string | null;
  /** Whether the drawer for `id` should be open. */
  isOpen: (id: string) => boolean;
  /** Open the drawer for `id` (pushes a history entry — back closes it). */
  open: (id: string, extra?: Readonly<Record<string, DrillValue>>) => void;
  /** Close the drawer (removes the param). */
  close: () => void;
}

/**
 * URL-held `Drawer` state: one search param carries which entity's detail
 * drawer is open, so an L2 drawer view is shareable and the back button
 * closes it (dashboard design rules §1/§4).
 *
 * ```tsx
 * const drawer = useDrawerParam("alert");
 * <button onClick={() => drawer.open(alert.id)}>…</button>
 * <DetailDrawer open={drawer.value === alert.id} onOpenChange={(o) => !o && drawer.close()} …/>
 * ```
 */
export function useDrawerParam(key: string): DrawerParam {
  const [searchParams, setSearchParams] = useSearchParams();
  const value = searchParams.get(key);

  const open = useCallback(
    (id: string, extra?: Readonly<Record<string, DrillValue>>) => {
      setSearchParams((previous) => {
        const next = new URLSearchParams(previous);
        next.set(key, id);
        for (const [extraKey, extraValue] of Object.entries(extra ?? {})) {
          const serialized = serializeDrillValue(extraValue);
          if (serialized == null) {
            next.delete(extraKey);
          } else {
            next.set(extraKey, serialized);
          }
        }
        return next;
      });
    },
    [key, setSearchParams],
  );

  const close = useCallback(() => {
    setSearchParams((previous) => {
      const next = new URLSearchParams(previous);
      next.delete(key);
      return next;
    });
  }, [key, setSearchParams]);

  const isOpen = useCallback((id: string) => value === id, [value]);

  return { value, isOpen, open, close };
}
